import { ReportRequest } from './reporting.types';

/** Policy Analytics UI date keys that map to reporting date filter fields. */
const DATE_UI_TO_REPORTING: Record<string, string> = {
  'submitted-date': 'submittedAt',
  'created-date': 'createdAt',
  'approved-date': 'approvedAt',
  'canceled-date': 'canceledAt',
  'terminated-date': 'terminatedAt',
  'effective-date': 'effectiveDate',
  'last-activity-date': 'lastActivityAt',
  'inforce-date': 'inforceAt',
};

/** Policy Analytics UI multi-select keys → reporting filter field keys. */
const ID_ARRAY_UI_TO_REPORTING: Record<string, string> = {
  'agency-id': 'agency.id',
  'agent-id': 'agent.id',
  'carrier-id': 'enrollment.carrier.id',
  'base-category': 'baseCategory',
  status: 'manualStatus',
  'status-category': 'statusCategory',
  'consumer-state': 'lead.address.state',
  'permanent-tags': 'permanentTags.list',
  // Tags carried by the agent, not by the policy. Resolved to agent ids
  // server-side, which is why the target is a reserved key and not a field.
  'agent-tags': '_agentTags',
  'call-queue-id': 'call.queue.id',
  'call-campaign-name': 'call.campaign.name',
  'call-list-name': 'call.list.name',
  'call-type': 'call.callType',
};

/**
 * "Is not" multi-select keys → the field each one negates, sent under
 * `_mustNot` so the server leaves out exactly what the include would keep.
 */
const NEGATED_ID_ARRAY_UI_TO_REPORTING: Record<string, string> = {
  'not-permanent-tags': 'permanentTags.list',
  'not-agent-tags': '_agentTags',
  'not-call-queue-id': 'call.queue.id',
};

/** Scalar / boolean UI keys → reporting filter field keys. */
const SCALAR_UI_TO_REPORTING: Record<string, string> = {
  'agent-npn': 'agent.npn',
  'metrics.hasEnrollment': 'metrics.hasEnrollment',
};

/** Numeric range UI keys → reporting filter field keys. */
const RANGE_UI_TO_REPORTING: Record<string, string> = {
  'metrics.totalCalls': 'metrics.totalCalls',
  'metrics.totalPayouts': 'metrics.totalPayouts',
};

const DATE_PRESETS = new Set([
  'today',
  'tomorrow',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'thisQuarter',
  'thisYear',
  'lastMonth',
  'last7days',
  'last30days',
  'last90days',
  'last365days',
  'olderThanDays',
  'olderThanHours',
  'nextHours',
  'lastHours',
  'nextDays',
  'lastDays',
  'nextMonth',
  'nextMonths',
  'nextYears',
]);

/** UI / picker date shape accepted by {@link buildReportingDateFilter}. */
export interface UiDateFilter {
  id?: string;
  range?: string;
  rangeType?: string;
  timeframe?: number;
  start?: string;
  end?: string;
  isRelative?: boolean;
  /** IANA timezone used when interpreting calendar-day denominators. */
  timeZone?: string;
}

export type PolicyEventType = 'submitted' | 'wentActive' | 'canceledTerminated';

/** Reporting date filter keys used for timeframe event switching. */
export type PolicyEventDateField =
  'submittedAt' | 'approvedAt' | 'inforceAt' | 'canceledAt' | 'terminatedAt';

export const POLICY_EVENT_OPTIONS: ReadonlyArray<{
  id: PolicyEventType;
  label: string;
  description: string;
}> = [
  {
    id: 'submitted',
    label: 'Submitted',
    description: 'Policies submitted in the selected timeframe',
  },
  {
    id: 'wentActive',
    label: 'Went active',
    description: 'Approved or inforce in the selected timeframe',
  },
  {
    id: 'canceledTerminated',
    label: 'Canceled / terminated',
    description: 'Canceled or terminated in the selected timeframe',
  },
];

const EVENT_DATE_FIELDS: Record<PolicyEventType, PolicyEventDateField[]> = {
  submitted: ['submittedAt'],
  wentActive: ['approvedAt', 'inforceAt'],
  canceledTerminated: ['canceledAt', 'terminatedAt'],
};

function toIsoString(
  value: string | number | Date | undefined
): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return new Date(value).toISOString();
  if (value instanceof Date) return value.toISOString();
  return undefined;
}

/**
 * Translate a UI date object into the Reporting API date-filter shape
 * (`{ range, start?, end?, timeframe?, timeZone? }`).
 */
export function buildReportingDateFilter(
  date?: UiDateFilter | null,
  timezone?: string
): Record<string, unknown> | undefined {
  if (!date) {
    return undefined;
  }

  const id = date.range ?? date.rangeType ?? date.id;
  const start = toIsoString(date.start as string | number | Date | undefined);
  const end = toIsoString(date.end as string | number | Date | undefined);

  let out: Record<string, unknown> | undefined;
  if (id && DATE_PRESETS.has(id)) {
    out = { range: id };
    if (typeof date.timeframe === 'number' && date.timeframe > 0) {
      out['timeframe'] = date.timeframe;
    }
  } else if (start != null || end != null) {
    out = { range: 'custom' };
    if (start != null) out['start'] = start;
    if (end != null) out['end'] = end;
  }

  if (!out) {
    return undefined;
  }
  if (timezone) {
    out['timeZone'] = timezone;
  }
  return out;
}

/**
 * The calendar day `date` buckets are keyed by, in the reporting timezone.
 *
 * `toISOString().slice(0, 10)` was UTC, so for several hours each evening a
 * New York caller excluded tomorrow's date and kept the partial day it
 * meant to drop.
 */
export function reportingDayString(
  timezone?: string,
  now: Date = new Date()
): string {
  if (!timezone) {
    return now.toISOString().slice(0, 10);
  }
  try {
    // en-CA renders as YYYY-MM-DD, which is the shape `date` buckets use.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Day-of-week / day-of-month / month for a date in the reporting timezone. */
function reportingParts(
  timezone: string | undefined,
  at: Date
): { weekday: number; day: number; month: number } {
  const iso = reportingDayString(timezone, at);
  const [year, month, day] = iso.split('-').map(Number);
  // Midday UTC keeps the weekday stable regardless of the offset applied.
  const weekday = new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
  return { weekday, day, month };
}

/**
 * Whether the window covers only the current, still-accumulating day.
 *
 * Preset names cannot be trusted on their own *or* dismissed: `thisWeek` on
 * a Sunday and `thisMonth` on the 1st are today-only in practice even though
 * the name suggests a span, and the resolved filter keeps the keyword rather
 * than expanding it to dates. So calendar-relative presets are checked
 * against where in the period today actually falls.
 *
 * Anything unrecognised is treated as extending beyond today, which keeps
 * the exclusion — the conservative direction, since that is the behaviour
 * every multi-day window already had.
 */
export function isCurrentDayOnlyWindow(
  resolved: Record<string, unknown> | undefined,
  timezone?: string,
  now: Date = new Date()
): boolean {
  if (!resolved) {
    return false;
  }
  const range = resolved['range'];
  if (range === 'today') {
    return true;
  }
  if (range === 'thisWeek' || range === 'thisMonth' || range === 'thisYear') {
    const { weekday, day, month } = reportingParts(timezone, now);
    if (range === 'thisWeek') {
      return weekday === 0;
    }
    if (range === 'thisMonth') {
      return day === 1;
    }
    return day === 1 && month === 1;
  }
  if (range !== 'custom') {
    return false;
  }
  const start = resolved['start'];
  const end = resolved['end'];
  if (typeof start !== 'string' || typeof end !== 'string') {
    return false;
  }
  const today = reportingDayString(timezone, now);
  const dayOf = (iso: string): string => {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime())
      ? ''
      : reportingDayString(timezone, parsed);
  };
  return dayOf(start) === today && dayOf(end) === today;
}

function asIdArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }
  const ids = value
    .map(entry => {
      if (typeof entry === 'string') return entry;
      if (
        entry &&
        typeof entry === 'object' &&
        'id' in entry &&
        typeof (entry as { id: unknown }).id === 'string'
      ) {
        return (entry as { id: string }).id;
      }
      return undefined;
    })
    .filter((id): id is string => Boolean(id));
  return ids.length > 0 ? ids : undefined;
}

export interface PolicyReportingQueryParts {
  filter: Record<string, unknown>;
  exclude?: Record<string, unknown>;
}

/**
 * Translate Policy Analytics `buildFilterObject()` output into reporting
 * filter / exclude keys (`agency.id`, `baseCategory`, `submittedAt`, …).
 * Only keys supported by the policies reporting index config are mapped.
 */
export function buildPolicyReportingQuery(
  uiFilter: Record<string, unknown> | null | undefined,
  options?: { timezone?: string; omitDateKeys?: ReadonlySet<string> }
): PolicyReportingQueryParts {
  const apiFilter: Record<string, unknown> = {};
  if (!uiFilter) {
    return { filter: apiFilter };
  }

  const omitDates = options?.omitDateKeys;
  const mustNot: Record<string, unknown> = {};

  for (const [uiKey, uiValue] of Object.entries(uiFilter)) {
    if (uiValue == null) {
      continue;
    }

    if (uiKey === 'exclude-status') {
      continue;
    }

    const negatedField = NEGATED_ID_ARRAY_UI_TO_REPORTING[uiKey];
    if (negatedField) {
      const ids = asIdArray(uiValue);
      if (ids) {
        mustNot[negatedField] = ids;
      }
      continue;
    }

    const idField = ID_ARRAY_UI_TO_REPORTING[uiKey];
    if (idField) {
      const ids = asIdArray(uiValue);
      if (ids) {
        apiFilter[idField] = ids;
      }
      continue;
    }

    const scalarField = SCALAR_UI_TO_REPORTING[uiKey];
    if (scalarField) {
      if (typeof uiValue === 'string' && uiValue.trim()) {
        apiFilter[scalarField] = uiValue.trim();
      } else if (typeof uiValue === 'boolean') {
        apiFilter[scalarField] = uiValue;
      }
      continue;
    }

    const rangeField = RANGE_UI_TO_REPORTING[uiKey];
    if (rangeField) {
      if (
        typeof uiValue === 'object' &&
        !Array.isArray(uiValue) &&
        ('min' in uiValue || 'max' in uiValue || 'empty' in uiValue)
      ) {
        apiFilter[rangeField] = uiValue;
      }
      continue;
    }

    const dateField = DATE_UI_TO_REPORTING[uiKey];
    if (dateField) {
      if (omitDates?.has(uiKey) || omitDates?.has(dateField)) {
        continue;
      }
      const date = buildReportingDateFilter(
        uiValue as UiDateFilter,
        options?.timezone
      );
      if (date) {
        apiFilter[dateField] = date;
      }
    }
  }

  if (Object.keys(mustNot).length > 0) {
    apiFilter['_mustNot'] = mustNot;
  }

  const excludeStatuses = asIdArray(uiFilter['exclude-status']);
  const exclude =
    excludeStatuses && excludeStatuses.length > 0
      ? { manualStatus: excludeStatuses }
      : undefined;

  return exclude ? { filter: apiFilter, exclude } : { filter: apiFilter };
}

/**
 * Translate Policy Analytics `buildFilterObject()` output into reporting
 * filter keys (`agency.id`, `baseCategory`, `submittedAt`, …).
 */
export function buildPolicyReportingFilter(
  uiFilter: Record<string, unknown> | null | undefined,
  options?: { timezone?: string; omitDateKeys?: ReadonlySet<string> }
): Record<string, unknown> {
  return buildPolicyReportingQuery(uiFilter, options).filter;
}

/**
 * Prefer submitted-date, then other UI date fields, as the Overview
 * "timeframe window" used when switching event types.
 */
export function extractPolicyTimeframeWindow(
  uiFilter: Record<string, unknown> | null | undefined
): UiDateFilter | undefined {
  if (!uiFilter) {
    return undefined;
  }
  const preferredKeys = [
    'submitted-date',
    'created-date',
    'approved-date',
    'last-activity-date',
    'effective-date',
    'canceled-date',
    'terminated-date',
  ];
  for (const key of preferredKeys) {
    const value = uiFilter[key];
    if (value && typeof value === 'object') {
      return value as UiDateFilter;
    }
  }
  return undefined;
}

function computeExtendedBoundsMs(
  date?: UiDateFilter
): { min: number; max: number } | undefined {
  if (!date?.start || !date?.end) {
    return undefined;
  }
  const min = Date.parse(date.start);
  const max = Date.parse(date.end);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return undefined;
  }
  return { min, max };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function calendarDayOrdinal(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find(part => part.type === type)?.value);
  return Date.UTC(value('year'), value('month') - 1, value('day')) / MS_PER_DAY;
}

/**
 * Daily buckets stay readable through ~2 months; beyond that the chart
 * switches to weekly aggregation (e.g. last90days / thisYear).
 */
export const SALES_TIMELINE_DAY_INTERVAL_MAX_SPAN_DAYS = 62;

export type SalesTimelineHistogramInterval = 'day' | 'week';

/**
 * Estimate the filter window length in days from custom start/end or
 * known presets. Returns undefined when the span cannot be inferred.
 */
export function estimateSalesTimelineSpanDays(
  date?: UiDateFilter | null,
  timeZone?: string
): number | undefined {
  if (!date) {
    return undefined;
  }

  if (date.start != null && date.end != null) {
    const min = Date.parse(date.start);
    const max = Date.parse(date.end);
    if (Number.isFinite(min) && Number.isFinite(max) && max >= min) {
      const reportingTimeZone = timeZone ?? date.timeZone;
      if (reportingTimeZone) {
        const endForCounting = max > min ? new Date(max - 1) : new Date(max);
        return Math.max(
          1,
          calendarDayOrdinal(endForCounting, reportingTimeZone) -
            calendarDayOrdinal(new Date(min), reportingTimeZone) +
            1
        );
      }
      return (max - min) / MS_PER_DAY;
    }
  }

  const id = date.range ?? date.rangeType ?? date.id;
  switch (id) {
    case 'today':
    case 'tomorrow':
    case 'yesterday':
      return 1;
    case 'thisWeek':
    case 'lastWeek':
    case 'last7days':
      return 7;
    case 'last30days':
      return 30;
    case 'last90days':
      return 90;
    case 'last365days':
      return 365;
    case 'thisMonth':
    case 'lastMonth':
    case 'nextMonth':
      return 31;
    case 'thisYear':
      return 366;
    case 'lastDays':
    case 'nextDays':
    case 'olderThanDays':
      return typeof date.timeframe === 'number' && date.timeframe > 0
        ? date.timeframe
        : undefined;
    case 'lastHours':
    case 'nextHours':
    case 'olderThanHours':
      return typeof date.timeframe === 'number' && date.timeframe > 0
        ? date.timeframe / 24
        : undefined;
    case 'nextMonths':
      return typeof date.timeframe === 'number' && date.timeframe > 0
        ? date.timeframe * 31
        : undefined;
    case 'nextYears':
      return typeof date.timeframe === 'number' && date.timeframe > 0
        ? date.timeframe * 366
        : undefined;
    default:
      return undefined;
  }
}

/**
 * Pick day vs week histogram interval from the submitted-date window.
 * Unknown / missing span defaults to day (short-window assumption).
 */
export function selectSalesTimelineHistogramInterval(
  date?: UiDateFilter | null
): SalesTimelineHistogramInterval {
  const spanDays = estimateSalesTimelineSpanDays(date);
  if (spanDays == null) {
    return 'day';
  }
  return spanDays <= SALES_TIMELINE_DAY_INTERVAL_MAX_SPAN_DAYS ? 'day' : 'week';
}

const EVENT_TIMELINE_LABEL: Record<PolicyEventType, string> = {
  submitted: 'Submitted',
  wentActive: 'Went active',
  canceledTerminated: 'Canceled / terminated',
};

export function salesTimelineChartTitle(
  interval: SalesTimelineHistogramInterval,
  eventType: PolicyEventType = 'submitted'
): string {
  return `${EVENT_TIMELINE_LABEL[eventType]} policies per ${interval} by product type`;
}

export function salesTimelineAxisTitle(
  interval: SalesTimelineHistogramInterval,
  eventType: PolicyEventType = 'submitted'
): string {
  const label = EVENT_TIMELINE_LABEL[eventType];
  return interval === 'week' ? `${label} week` : `${label} date`;
}

/**
 * Primary date-histogram field for an event. Multi-field events
 * (`wentActive`, `canceledTerminated`) still filter with `_should`, but the
 * histogram buckets on the first mapped field.
 */
export function eventTimelineHistogramField(
  eventType: PolicyEventType
): `${PolicyEventDateField}.timestamp` {
  return `${EVENT_DATE_FIELDS[eventType][0]}.timestamp`;
}

export interface BuildPolicySalesTimelineOptions {
  filter?: Record<string, unknown> | null;
  /** Defaults to `submitted`. Timeline field + filter follow this event. */
  eventType?: PolicyEventType;
  timezone?: string;
  groupName?: string;
}

/**
 * Date histogram nested by `baseCategory`, metric `totalPolicies`.
 * Histogram field and date filter follow {@link eventType}
 * (default submitted). Interval is day for short windows and week
 * for longer ones (see {@link selectSalesTimelineHistogramInterval}).
 */
export function buildPolicySalesTimelineRequest(
  options: BuildPolicySalesTimelineOptions = {}
): ReportRequest {
  const eventType = options.eventType ?? 'submitted';
  const groupName = options.groupName ?? 'salesByDayCategory';
  const dateFields = EVENT_DATE_FIELDS[eventType];
  const window = extractPolicyTimeframeWindow(options.filter);
  const dateFilter = buildReportingDateFilter(window, options.timezone);
  const bounds = computeExtendedBoundsMs(window);
  const interval = selectSalesTimelineHistogramInterval(window);

  // Drop UI date keys so the event date field alone drives the window.
  const omitDateKeys = new Set(Object.keys(DATE_UI_TO_REPORTING));
  const { filter: baseFilter, exclude } = buildPolicyReportingQuery(
    options.filter,
    {
      timezone: options.timezone,
      omitDateKeys,
    }
  );

  const filter: Record<string, unknown> = { ...baseFilter };
  if (dateFilter) {
    if (dateFields.length === 1) {
      filter[dateFields[0]] = dateFilter;
    } else {
      filter['_should'] = dateFields.map(field => ({
        [field]: dateFilter,
      }));
    }
  }

  return {
    sources: [{ index: 'policies', metrics: ['totalPolicies'] }],
    filter,
    ...(exclude ? { exclude } : {}),
    groups: [
      {
        name: groupName,
        dimensions: [
          {
            field: eventTimelineHistogramField(eventType),
            dateHistogram: {
              interval,
              ...(options.timezone ? { timeZone: options.timezone } : {}),
              ...(bounds ? { extendedBounds: bounds } : {}),
            },
          },
          { field: 'baseCategory', size: 20 },
        ],
        metrics: ['totalPolicies'],
      },
    ],
  };
}

/** UI filter keys that represent write / lifecycle event dates. */
export const POLICY_WRITE_EVENT_DATE_UI_KEYS = [
  'submitted-date',
  'created-date',
  'approved-date',
  'canceled-date',
  'terminated-date',
  'last-activity-date',
  'effective-date',
  'inforce-date',
  'declined-date',
  'termination-schedule-date',
] as const;

/**
 * Strip write/event date keys so Analytics Full-book KPIs/charts reflect the
 * current book snapshot rather than a production window.
 */
export function stripPolicyWriteEventDates(
  filter: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!filter) {
    return {};
  }
  const out: Record<string, unknown> = { ...filter };
  for (const key of POLICY_WRITE_EVENT_DATE_UI_KEYS) {
    delete out[key];
  }
  return out;
}

/**
 * Default Production period — never silently all-time.
 * Matches date-dropdown storage (`lastDays` + timeframe) so the pinned
 * filter chip initializes to "Last 30 Days" like dialer callDate defaults.
 */
export const DEFAULT_PRODUCTION_PERIOD: UiDateFilter = {
  range: 'lastDays',
  rangeType: 'lastDays',
  timeframe: 30,
  isRelative: true,
};

/** Period presets for Reporting — no All Time (Production is always windowed). */
export const PRODUCTION_PERIOD_PRESETS = {
  quick: [
    { id: 'today', title: 'Today', group: 'current' as const },
    { id: 'yesterday', title: 'Yesterday', group: 'past' as const },
    { id: 'thisWeek', title: 'This Week', group: 'current' as const },
    { id: 'lastWeek', title: 'Last Week', group: 'past' as const },
    { id: 'last7days', title: 'Last 7 Days', group: 'past' as const },
    { id: 'last30days', title: 'Last 30 Days', group: 'past' as const },
    { id: 'lastMonth', title: 'Last Month', group: 'past' as const },
  ],
  advanced: [
    { id: 'thisMonth', title: 'This Month', group: 'current' as const },
    { id: 'last90days', title: 'Last 90 Days', group: 'past' as const },
    { id: 'last365days', title: 'Last 365 Days', group: 'past' as const },
    { id: 'thisYear', title: 'This Year', group: 'current' as const },
    // Resolved by the date dropdown to the absolute Oct 15 to Dec 7 window.
    { id: 'aep', title: 'AEP (Oct 15 to Dec 7)', group: 'past' as const },
    { id: 'lastDays', title: 'Last X Days...', group: 'relative' as const },
  ],
  custom: { id: 'custom', title: 'Pick dates...' },
};

/** URL query value ↔ {@link PolicyEventType} for the Reporting page. */
export const POLICY_REPORTING_EVENT_QUERY_PARAM = 'event';

const EVENT_TYPE_TO_URL: Record<PolicyEventType, string> = {
  submitted: 'submitted',
  wentActive: 'went-active',
  canceledTerminated: 'canceled-terminated',
};

/**
 * Accepts both the URL form (`went-active`) and the event id itself
 * (`wentActive`): widget configs store the id, the URL stores the slug.
 */
const URL_TO_EVENT_TYPE: Record<string, PolicyEventType> = {
  submitted: 'submitted',
  wentActive: 'wentActive',
  'went-active': 'wentActive',
  canceledTerminated: 'canceledTerminated',
  'canceled-terminated': 'canceledTerminated',
};

export function policyReportingEventToUrl(eventType: PolicyEventType): string {
  return EVENT_TYPE_TO_URL[eventType];
}

export function parsePolicyReportingEvent(
  value: unknown
): PolicyEventType | null {
  if (typeof value !== 'string' || !value) {
    return null;
  }
  return Object.prototype.hasOwnProperty.call(URL_TO_EVENT_TYPE, value)
    ? URL_TO_EVENT_TYPE[value]
    : null;
}

export interface BuildPolicyEventTotalsOptions {
  filter?: Record<string, unknown> | null;
  eventType: PolicyEventType;
  timezone?: string;
  groupName?: string;
}

/**
 * Apply the selected timeframe window to the event date field(s) and
 * group by `baseCategory` for product-type breakdown.
 *
 * Multi-field events (`wentActive`, `canceledTerminated`) use `_should`
 * so a policy matching either date in-window is counted once.
 */
export function buildPolicyEventTotalsRequest(
  options: BuildPolicyEventTotalsOptions
): ReportRequest {
  const groupName = options.groupName ?? 'byBaseCategory';
  const dateFields = EVENT_DATE_FIELDS[options.eventType];
  const window = extractPolicyTimeframeWindow(options.filter);
  const dateFilter = buildReportingDateFilter(window, options.timezone);

  // Drop UI date keys so the event date field alone drives the window.
  const omitDateKeys = new Set(Object.keys(DATE_UI_TO_REPORTING));
  const { filter: baseFilter, exclude } = buildPolicyReportingQuery(
    options.filter,
    {
      timezone: options.timezone,
      omitDateKeys,
    }
  );

  const filter: Record<string, unknown> = { ...baseFilter };
  if (dateFilter) {
    if (dateFields.length === 1) {
      filter[dateFields[0]] = dateFilter;
    } else {
      filter['_should'] = dateFields.map(field => ({
        [field]: dateFilter,
      }));
    }
  }

  return {
    sources: [{ index: 'policies', metrics: ['totalPolicies'] }],
    filter,
    ...(exclude ? { exclude } : {}),
    groups: [
      {
        name: groupName,
        dimensions: [{ field: 'baseCategory', size: 20 }],
        metrics: ['totalPolicies'],
      },
    ],
  };
}

export function eventDateFieldsFor(
  eventType: PolicyEventType
): readonly PolicyEventDateField[] {
  return EVENT_DATE_FIELDS[eventType];
}
